import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys } from '@/lib/queryKeys';
import { invalidateInvoicePaymentViews } from './invalidateInvoicePaymentViews';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import { uuidv7 } from '@/lib/api/clients/billing-v2';
import { getActiveTenantId } from '@/lib/activeTenant';
import { ApiError } from '@/lib/api/errors';
import { isAcceptedBankDebit } from '@/lib/payments/savedPaymentMethods';

// Keep unresolved operations across dialog remounts in this browser session.
// A lost response must replay the original charge, not create another one.
const pendingChargeKeys = new Map<string, string>();

interface ChargeSavedInvoicePaymentDto {
  invoiceId: string;
  amount: number;
  savedPaymentMethodId: string;
  /**
   * Omitted by the "charge a new/saved method" collector (BR-77): the server
   * resolves the provider itself from the saved method it just charged, and
   * only cross-checks a caller-supplied value when one is given.
   */
  provider?: string;
  /**
   * The saved method's stored type (CREDIT_CARD / DEBIT_CARD / ACH /
   * BANK_ACCOUNT), used only to pick the wire rail below.
   */
  paymentMethodType?: string | null;
  /** One-time-use collector option (BR-77 staff "charge a new card/bank
   * account" dialog). Stamped server-side into gatewayMetadata.staffCollect. */
  removeAfterCharge?: boolean;
  /**
   * The method was entered by staff for this charge, not picked from the
   * contact's saved methods. The server requires `achAuthorization` for a
   * newly entered bank account; a saved one picked by staff sends
   * `staffInitiated` instead.
   */
  newlyEnteredMethod?: boolean;
  /**
   * Staff started this charge by hand (the dialog's "Charge Saved Method").
   * The server requires `achAuthorization` when it is a bank debit.
   */
  staffInitiated?: boolean;
  /** The customer's authorization to debit a bank account — required by the
   * server for a staff-started (`newlyEnteredMethod` / `staffInitiated`)
   * bank-rail charge. */
  achAuthorization?: {
    channel: 'IN_PERSON' | 'PHONE' | 'WRITTEN';
    text: string;
    acceptedAt: string;
  };
  /**
   * Caller-managed idempotency key. When given, this hook sends it as-is and
   * does NOT consult its own signature-based key cache below — the caller
   * (`chargeNewMethod` in InvoiceActions.tsx) holds the key itself across
   * retries, exactly like the contact dialog's `newMethodChargeAttemptRef`,
   * because a retry after an ambiguous outcome must replay the SAME key and
   * a retry after a decline must mint a fresh one — a distinction this
   * hook's body-derived signature cannot make on its own.
   */
  idempotencyKey?: string;
}

interface UseChargeSavedInvoicePaymentOptions {
  /**
   * Suppress this hook's own generic result toast ("Saved payment method
   * charged" / "Bank payment submitted" / "Payment is pending..."). Query
   * invalidation still runs either way. For a caller that shows its own more
   * specific copy for the same result (the new-method-charge collector,
   * which needs the exact dollar amount in the message) — set this to
   * `false` so the operator doesn't see two toasts stack for one charge.
   * Defaults to `true`, matching every caller before this option existed.
   */
  showSuccessToast?: boolean;
  /**
   * Suppress `useApiMutation`'s own generic error toast. For a caller that
   * classifies the failure itself (a decline vs. an ambiguous "we don't know
   * if this went through" outcome) and shows its own copy for each, so staff
   * never see two conflicting messages for one failed charge. Defaults to
   * `true`, matching every caller before this option existed.
   */
  showErrorToast?: boolean;
}

/**
 * The rail this saved method actually charges on.
 *
 * The dialog offers bank accounts alongside cards, so hardcoding `CARD` sent a
 * bank charge up labelled as a card. The server does not price off this value —
 * it re-resolves the rail from the stored method and refines card funding with
 * the provider before deciding on a service fee — but a request that says
 * "card" about a bank account is a lie that every log, replay and future reader
 * has to see through, and it is exactly what makes "does ACH get surcharged?"
 * unanswerable from the wire. The endpoint's enum is CARD / ACH / CARD_PRESENT,
 * so a debit card rides the CARD rail here just as it does at Stripe.
 */
function wireRailFor(paymentMethodType?: string | null): 'CARD' | 'ACH' {
  const normalized = String(paymentMethodType ?? '')
    .trim()
    .toUpperCase();
  return normalized === 'ACH' || normalized === 'BANK_ACCOUNT' ? 'ACH' : 'CARD';
}

export function useChargeSavedInvoicePayment(options: UseChargeSavedInvoicePaymentOptions = {}) {
  const queryClient = useQueryClient();
  const showSuccessToast = options.showSuccessToast ?? true;

  return useApiMutation<Record<string, unknown>, ChargeSavedInvoicePaymentDto>({
    showErrorToast: options.showErrorToast ?? true,
    mutationFn: async ({
      invoiceId,
      amount,
      savedPaymentMethodId,
      provider,
      paymentMethodType,
      removeAfterCharge,
      newlyEnteredMethod,
      staffInitiated,
      achAuthorization,
      idempotencyKey: explicitIdempotencyKey,
    }) => {
      const body = {
        amount,
        paymentMethod: wireRailFor(paymentMethodType),
        savedPaymentMethodId,
        provider,
        ...(removeAfterCharge !== undefined ? { removeAfterCharge } : {}),
        ...(newlyEnteredMethod ? { newlyEnteredMethod: true } : {}),
        ...(staffInitiated ? { staffInitiated: true } : {}),
        ...(achAuthorization ? { achAuthorization } : {}),
      };
      // A caller-managed key (the new-method-charge collector) is sent as-is
      // and never touches the signature cache below — that cache exists so
      // the pre-existing "charge a saved method" flow can survive a lost
      // response with no key of its own to hold, but it cannot tell an
      // ambiguous outcome (replay the same key) from a decline (mint a fresh
      // one), which the new-method flow's own ref does.
      const signature = explicitIdempotencyKey
        ? null
        : JSON.stringify([getActiveTenantId(), invoiceId, body]);
      const idempotencyKey =
        explicitIdempotencyKey ??
        (signature ? pendingChargeKeys.get(signature) : undefined) ??
        `invoice-saved-charge:${uuidv7()}`;
      if (signature) {
        pendingChargeKeys.set(signature, idempotencyKey);
      }

      const payment = await apiPost<Record<string, unknown>, Record<string, unknown>>(
        operationsClient,
        invoiceEndpoints.gatewayPayment(invoiceId),
        { ...body, idempotencyKey },
      );
      // HTTP success alone is not a completed charge: PENDING and FAILED are
      // also returned here. Retain those keys for the backend's retry handling.
      // An accepted bank debit is the exception: its outcome is known and it
      // only has to clear, so keeping the key would turn the next same-amount
      // charge into a replay of this one — reported as charged, charging nothing.
      const resolved =
        payment.status === 'SUCCEEDED' || isAcceptedBankDebit(payment, paymentMethodType);
      if (signature && resolved && pendingChargeKeys.get(signature) === idempotencyKey) {
        pendingChargeKeys.delete(signature);
      }
      if (payment.status === 'FAILED') {
        throw new ApiError(
          422,
          'VALIDATION_ERROR',
          typeof payment.failureReason === 'string' && payment.failureReason.trim()
            ? payment.failureReason
            : 'Payment failed. Please check the payment method and try again.',
        );
      }
      return payment;
    },
    onSuccess: (payment, dto) => {
      if (!showSuccessToast) return;
      if (payment.status === 'SUCCEEDED') {
        toast.success('Saved payment method charged');
      } else if (isAcceptedBankDebit(payment, dto.paymentMethodType)) {
        toast.success('Bank payment submitted', {
          description:
            'Bank payments usually clear in a few business days. It counts as paid while it clears; if the bank returns it, the balance goes back up.',
        });
      } else if (payment.status === 'PENDING') {
        toast.info('Payment is pending. Check the invoice Payments tab for its status.');
      } else {
        toast.info('Payment is not confirmed. Check the invoice Payments tab for its status.');
      }
    },
    // A resolved provider failure is still persisted and must appear in Payments.
    onSettled: async (_data, _error, dto) => {
      await queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(dto.invoiceId),
      });
      await queryClient.invalidateQueries({
        queryKey: [...invoiceKeys.detail(dto.invoiceId), 'payments'],
      });
      await queryClient.invalidateQueries({
        queryKey: invoiceKeys.lists(),
      });
      await invalidateInvoicePaymentViews(queryClient);
    },
  });
}
